/// Soundings — a battleships / minesweeper hybrid set on a paper
/// chart. The player searches a 10 × 10 grid for a hidden fleet
/// while avoiding hidden hazards (rocks / shoals). Most hazards
/// are marked by an IALA cardinal buoy in an adjacent cell — the
/// mark points toward safe water, so the hazard sits in the
/// opposite direction. A small minority of hazards are unmarked,
/// adding genuine risk to the search.
///
/// All game logic lives here as pure data + deterministic
/// transitions; the apps/web layer just renders state and feeds
/// click events back through `revealCell`.

export type Direction = "N" | "E" | "S" | "W";

/// Vessels in the fleet. Lengths drawn from the standard battleships
/// distribution but named after IRPCS vessel categories.
export interface ShipSpec {
  /// Short id used internally and in the sink-status panel.
  id: string;
  /// Display name shown to the player.
  name: string;
  /// Number of cells the vessel occupies.
  length: number;
}

export const DEFAULT_FLEET: readonly ShipSpec[] = [
  { id: "tanker", name: "Tanker", length: 5 },
  { id: "frigate", name: "Frigate", length: 4 },
  { id: "corvette", name: "Corvette", length: 3 },
  { id: "cutter", name: "Cutter", length: 3 },
  { id: "patrol", name: "Patrol boat", length: 2 },
];

export type CellContent =
  | { kind: "water" }
  | { kind: "ship"; shipId: string }
  | { kind: "hazard" }
  | { kind: "cardinal"; direction: Direction }
  /// Dry land — part of a rocky enclave. Safe to reveal (revealing one
  /// land cell uncovers the whole connected landmass), but the rocks
  /// among/around it stay hidden.
  | { kind: "land" };

/// What the player sees on a cell. `hidden` cells haven't been
/// clicked yet; everything else is the result of clicking.
export type CellState =
  | "hidden"
  | "water"
  | "hit"
  | "sunk"
  | "grounded"
  | "cardinal"
  | "land";

export interface Cell {
  /// Truth — what's actually here.
  content: CellContent;
  /// View — what the player has uncovered.
  state: CellState;
}

export interface ShipState {
  spec: ShipSpec;
  /// Cell coordinates the ship occupies.
  cells: readonly { col: number; row: number }[];
  /// Number of cells already hit.
  hits: number;
  sunk: boolean;
}

export interface GameConfig {
  cols: number;
  rows: number;
  fleet: readonly ShipSpec[];
  /// Total hazards on the board.
  hazards: number;
  /// Of those, how many have a cardinal mark placed adjacent. The
  /// remainder are unmarked, forcing the player to take real risks.
  markedHazards: number;
  /// Lives — how many times the player can ground before losing.
  lives: number;
  /// Number of rocky enclaves — clusters of land with one or two
  /// rocks in/around them, each marked by a cardinal. Advanced levels
  /// only; defaults to 0.
  enclaves?: number;
  /// RNG seed; `null` uses Math.random.
  seed: number | null;
}

export const EASY_CONFIG: GameConfig = {
  cols: 8,
  rows: 8,
  fleet: [
    { id: "frigate", name: "Frigate", length: 4 },
    { id: "corvette", name: "Corvette", length: 3 },
    { id: "cutter", name: "Cutter", length: 2 },
  ],
  hazards: 3,
  markedHazards: 3,
  lives: 4,
  seed: null,
};

export const NORMAL_CONFIG: GameConfig = {
  cols: 10,
  rows: 10,
  fleet: DEFAULT_FLEET,
  hazards: 5,
  markedHazards: 4,
  lives: 3,
  seed: null,
};

export const HARD_CONFIG: GameConfig = {
  cols: 10,
  rows: 10,
  fleet: DEFAULT_FLEET,
  hazards: 7,
  markedHazards: 4,
  lives: 2,
  enclaves: 1,
  seed: null,
};

/// Bigger boards. The picker offers these as "Huge / Expert /
/// Ocean" so players who want a real search problem have room. At
/// 100 × 100 the visual grid is tiny on a phone — the coordinate
/// input field next to the grid is the practical way to fire.
export const HUGE_CONFIG: GameConfig = {
  cols: 30,
  rows: 30,
  fleet: [
    { id: "carrier", name: "Carrier", length: 6 },
    { id: "tanker", name: "Tanker", length: 5 },
    { id: "frigate1", name: "Frigate", length: 4 },
    { id: "frigate2", name: "Frigate", length: 4 },
    { id: "corvette1", name: "Corvette", length: 3 },
    { id: "corvette2", name: "Corvette", length: 3 },
    { id: "cutter", name: "Cutter", length: 3 },
    { id: "patrol1", name: "Patrol boat", length: 2 },
    { id: "patrol2", name: "Patrol boat", length: 2 },
  ],
  hazards: 18,
  markedHazards: 12,
  lives: 5,
  enclaves: 2,
  seed: null,
};

export const EXPERT_CONFIG: GameConfig = {
  cols: 50,
  rows: 50,
  fleet: [
    { id: "carrier1", name: "Carrier", length: 6 },
    { id: "carrier2", name: "Carrier", length: 6 },
    { id: "tanker1", name: "Tanker", length: 5 },
    { id: "tanker2", name: "Tanker", length: 5 },
    { id: "frigate1", name: "Frigate", length: 4 },
    { id: "frigate2", name: "Frigate", length: 4 },
    { id: "frigate3", name: "Frigate", length: 4 },
    { id: "corvette1", name: "Corvette", length: 3 },
    { id: "corvette2", name: "Corvette", length: 3 },
    { id: "corvette3", name: "Corvette", length: 3 },
    { id: "cutter1", name: "Cutter", length: 3 },
    { id: "cutter2", name: "Cutter", length: 3 },
    { id: "patrol1", name: "Patrol boat", length: 2 },
    { id: "patrol2", name: "Patrol boat", length: 2 },
    { id: "patrol3", name: "Patrol boat", length: 2 },
  ],
  hazards: 30,
  markedHazards: 20,
  lives: 6,
  enclaves: 3,
  seed: null,
};

export const OCEAN_CONFIG: GameConfig = {
  cols: 100,
  rows: 100,
  fleet: [
    { id: "carrier1", name: "Carrier", length: 6 },
    { id: "carrier2", name: "Carrier", length: 6 },
    { id: "carrier3", name: "Carrier", length: 6 },
    { id: "tanker1", name: "Tanker", length: 5 },
    { id: "tanker2", name: "Tanker", length: 5 },
    { id: "tanker3", name: "Tanker", length: 5 },
    { id: "frigate1", name: "Frigate", length: 4 },
    { id: "frigate2", name: "Frigate", length: 4 },
    { id: "frigate3", name: "Frigate", length: 4 },
    { id: "frigate4", name: "Frigate", length: 4 },
    { id: "corvette1", name: "Corvette", length: 3 },
    { id: "corvette2", name: "Corvette", length: 3 },
    { id: "corvette3", name: "Corvette", length: 3 },
    { id: "corvette4", name: "Corvette", length: 3 },
    { id: "corvette5", name: "Corvette", length: 3 },
    { id: "cutter1", name: "Cutter", length: 3 },
    { id: "cutter2", name: "Cutter", length: 3 },
    { id: "cutter3", name: "Cutter", length: 3 },
    { id: "patrol1", name: "Patrol boat", length: 2 },
    { id: "patrol2", name: "Patrol boat", length: 2 },
    { id: "patrol3", name: "Patrol boat", length: 2 },
    { id: "patrol4", name: "Patrol boat", length: 2 },
    { id: "patrol5", name: "Patrol boat", length: 2 },
    { id: "patrol6", name: "Patrol boat", length: 2 },
  ],
  hazards: 80,
  markedHazards: 50,
  lives: 8,
  enclaves: 5,
  seed: null,
};

/// Parse a chart-style coordinate label like "C7" or "AB42" into
/// 0-based column/row indices. Returns null for malformed input or
/// out-of-range coordinates. Column letters are case-insensitive
/// and use the same scheme as `coordLabel` (A=0, B=1, …, Z=25,
/// AA=26, AB=27, …) so the input scales to 100-wide boards.
export function parseCoord(
  input: string,
  cols: number,
  rows: number,
): { col: number; row: number } | null {
  const m = input.trim().toUpperCase().match(/^([A-Z]+)(\d+)$/);
  if (!m) return null;
  const letters = m[1]!;
  const number = parseInt(m[2]!, 10);
  let col = 0;
  for (let i = 0; i < letters.length; i++) {
    col = col * 26 + (letters.charCodeAt(i) - 64);
  }
  col -= 1;
  const row = number - 1;
  if (col < 0 || col >= cols) return null;
  if (row < 0 || row >= rows) return null;
  return { col, row };
}

export interface Board {
  config: GameConfig;
  cells: Cell[][]; // [row][col]
  ships: ShipState[];
  lives: number;
  shotsFired: number;
  status: "playing" | "won" | "lost";
}

// ─── RNG ─────────────────────────────────────────────────────────

/// Deterministic mulberry32 PRNG — used when a seed is supplied so
/// the same game can be replayed for testing.
function makeRng(seed: number | null): () => number {
  if (seed === null) return Math.random;
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleInPlace<T>(arr: T[], rng: () => number): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
}

// ─── Board generation ────────────────────────────────────────────

const DIR_OFFSETS: Record<Direction, { dCol: number; dRow: number }> = {
  // Cardinal points to "pass to N/E/S/W"; the hazard sits on the
  // OPPOSITE side. So if the cardinal is at row R and is a North
  // cardinal, the hazard is at row R+1 (one row south on screen).
  N: { dCol: 0, dRow: +1 },
  E: { dCol: -1, dRow: 0 },
  S: { dCol: 0, dRow: -1 },
  W: { dCol: +1, dRow: 0 },
};

const ALL_DIRECTIONS: readonly Direction[] = ["N", "E", "S", "W"];

export function newBoard(config: GameConfig = NORMAL_CONFIG): Board {
  const rng = makeRng(config.seed);

  // Empty board
  const cells: Cell[][] = [];
  for (let r = 0; r < config.rows; r++) {
    const row: Cell[] = [];
    for (let c = 0; c < config.cols; c++) {
      row.push({ content: { kind: "water" }, state: "hidden" });
    }
    cells.push(row);
  }

  // Place ships — random position + orientation, no overlap, no
  // wrap-around. Surrounding-buffer rules of classic battleships are
  // optional; we don't enforce them here so the player gets a real
  // search problem.
  const ships: ShipState[] = [];
  for (const spec of config.fleet) {
    const positions = placeShip(cells, spec.length, rng, config);
    if (!positions) {
      // Shouldn't happen with sensible configs, but bail rather than
      // loop forever.
      continue;
    }
    for (const p of positions) {
      cells[p.row]![p.col]!.content = { kind: "ship", shipId: spec.id };
    }
    ships.push({ spec, cells: positions, hits: 0, sunk: false });
  }

  // Scatter rocks + marking cardinals + rocky enclaves over the water.
  populateHazards(cells, rng, config);

  return {
    config,
    cells,
    ships,
    lives: config.lives,
    shotsFired: 0,
    status: "playing",
  };
}

/// Place rocky enclaves, then scatter `config.hazards` rocks over open
/// water and mark `config.markedHazards` of them with an adjacent
/// cardinal pointing away. Mutates `cells`. Shared by `newBoard` and
/// the manual two-player `finalizeBoard`.
function populateHazards(
  cells: Cell[][],
  rng: () => number,
  config: GameConfig,
): void {
  const enclaves = config.enclaves ?? 0;
  for (let i = 0; i < enclaves; i++) {
    placeEnclave(cells, rng, config);
  }

  const hazardPositions: { col: number; row: number }[] = [];
  let placed = 0;
  let safety = 0;
  while (placed < config.hazards && safety++ < 10_000) {
    const col = Math.floor(rng() * config.cols);
    const row = Math.floor(rng() * config.rows);
    const c = cells[row]![col]!;
    if (c.content.kind !== "water") continue;
    c.content = { kind: "hazard" };
    hazardPositions.push({ col, row });
    placed++;
  }

  const hazardOrder = [...hazardPositions];
  shuffleInPlace(hazardOrder, rng);
  let marksPlaced = 0;
  for (const h of hazardOrder) {
    if (marksPlaced >= config.markedHazards) break;
    const dirs = [...ALL_DIRECTIONS];
    shuffleInPlace(dirs, rng);
    let placedThis = false;
    for (const dir of dirs) {
      // Cardinal sits OPPOSITE the hazard from the safe-passage
      // direction (cardinal cell = hazard − DIR_OFFSETS[dir]).
      const off = DIR_OFFSETS[dir];
      const cCol = h.col - off.dCol;
      const cRow = h.row - off.dRow;
      if (cCol < 0 || cCol >= config.cols) continue;
      if (cRow < 0 || cRow >= config.rows) continue;
      const cell = cells[cRow]![cCol]!;
      if (cell.content.kind !== "water") continue;
      cell.content = { kind: "cardinal", direction: dir };
      placedThis = true;
      break;
    }
    if (placedThis) marksPlaced++;
  }
}

// ─── Manual placement (two-player setup) ─────────────────────────

function cloneCells(cells: Cell[][]): Cell[][] {
  return cells.map((row) =>
    row.map((c) => ({ content: { ...c.content }, state: c.state })),
  );
}

/// A blank board for manual ship placement — all water, hidden, no
/// ships, lives from the config.
export function emptyBoard(config: GameConfig): Board {
  const cells: Cell[][] = [];
  for (let r = 0; r < config.rows; r++) {
    const row: Cell[] = [];
    for (let c = 0; c < config.cols; c++) {
      row.push({ content: { kind: "water" }, state: "hidden" });
    }
    cells.push(row);
  }
  return {
    config,
    cells,
    ships: [],
    lives: config.lives,
    shotsFired: 0,
    status: "playing",
  };
}

/// Whether a ship of `length` fits at (col,row) in the given
/// orientation without leaving the board or overlapping another ship.
export function canPlaceShip(
  board: Board,
  length: number,
  col: number,
  row: number,
  horizontal: boolean,
): boolean {
  for (let i = 0; i < length; i++) {
    const c = horizontal ? col + i : col;
    const r = horizontal ? row : row + i;
    if (c < 0 || c >= board.config.cols || r < 0 || r >= board.config.rows) {
      return false;
    }
    if (board.cells[r]![c]!.content.kind === "ship") return false;
  }
  return true;
}

/// Place a fleet ship, returning a NEW board, or null if it doesn't fit
/// or that ship id is already placed.
export function placeFleetShip(
  board: Board,
  spec: ShipSpec,
  col: number,
  row: number,
  horizontal: boolean,
): Board | null {
  if (board.ships.some((s) => s.spec.id === spec.id)) return null;
  if (!canPlaceShip(board, spec.length, col, row, horizontal)) return null;
  const cells = cloneCells(board.cells);
  const positions: { col: number; row: number }[] = [];
  for (let i = 0; i < spec.length; i++) {
    const c = horizontal ? col + i : col;
    const r = horizontal ? row : row + i;
    cells[r]![c]!.content = { kind: "ship", shipId: spec.id };
    positions.push({ col: c, row: r });
  }
  return {
    ...board,
    cells,
    ships: [...board.ships, { spec, cells: positions, hits: 0, sunk: false }],
  };
}

/// Remove the ship occupying (col,row), if any, returning a NEW board.
export function removeShipAt(board: Board, col: number, row: number): Board {
  const cell = board.cells[row]?.[col];
  if (!cell || cell.content.kind !== "ship") return board;
  const shipId = cell.content.shipId;
  const ship = board.ships.find((s) => s.spec.id === shipId);
  if (!ship) return board;
  const cells = cloneCells(board.cells);
  for (const p of ship.cells) cells[p.row]![p.col]!.content = { kind: "water" };
  return {
    ...board,
    cells,
    ships: board.ships.filter((s) => s.spec.id !== shipId),
  };
}

/// A board with the whole fleet placed at random and no hazards — the
/// "Randomise my fleet" button in two-player setup.
export function randomFleet(config: GameConfig, seed: number | null = null): Board {
  const rng = makeRng(seed);
  const board = emptyBoard(config);
  for (const spec of config.fleet) {
    const positions = placeShip(board.cells, spec.length, rng, config);
    if (!positions) continue;
    for (const p of positions) {
      board.cells[p.row]![p.col]!.content = { kind: "ship", shipId: spec.id };
    }
    board.ships.push({ spec, cells: positions, hits: 0, sunk: false });
  }
  return board;
}

/// Finalise a manually-placed board for play: clone it and scatter the
/// rocks + cardinals (+ enclaves) around the placed ships. Returns a
/// NEW board ready to be bombed.
export function finalizeBoard(
  board: Board,
  seed: number | null = null,
): Board {
  const rng = makeRng(seed);
  const cells = cloneCells(board.cells);
  populateHazards(cells, rng, board.config);
  return { ...board, cells };
}

function placeShip(
  cells: Cell[][],
  length: number,
  rng: () => number,
  config: GameConfig,
): { col: number; row: number }[] | null {
  for (let attempt = 0; attempt < 200; attempt++) {
    const horizontal = rng() < 0.5;
    const maxCol = horizontal ? config.cols - length : config.cols - 1;
    const maxRow = horizontal ? config.rows - 1 : config.rows - length;
    const startCol = Math.floor(rng() * (maxCol + 1));
    const startRow = Math.floor(rng() * (maxRow + 1));
    const positions: { col: number; row: number }[] = [];
    let conflict = false;
    for (let i = 0; i < length; i++) {
      const col = horizontal ? startCol + i : startCol;
      const row = horizontal ? startRow : startRow + i;
      if (cells[row]![col]!.content.kind !== "water") {
        conflict = true;
        break;
      }
      positions.push({ col, row });
    }
    if (!conflict) return positions;
  }
  return null;
}

function neighbors4(col: number, row: number): { col: number; row: number }[] {
  return [
    { col: col + 1, row },
    { col: col - 1, row },
    { col, row: row + 1 },
    { col, row: row - 1 },
  ];
}

/// Place one rocky enclave: grow a small landmass on open water, then
/// put one or two rocks on its perimeter, each with a cardinal pointing
/// away (to the open-water side). Returns true if it placed anything.
function placeEnclave(
  cells: Cell[][],
  rng: () => number,
  config: GameConfig,
): boolean {
  const inBounds = (c: number, r: number) =>
    c >= 0 && c < config.cols && r >= 0 && r < config.rows;
  const isWater = (c: number, r: number) =>
    inBounds(c, r) && cells[r]![c]!.content.kind === "water";

  for (let attempt = 0; attempt < 100; attempt++) {
    const aCol = 1 + Math.floor(rng() * (config.cols - 2));
    const aRow = 1 + Math.floor(rng() * (config.rows - 2));
    if (!isWater(aCol, aRow)) continue;

    // Grow a land blob by randomly accreting adjacent water cells.
    const blob: { col: number; row: number }[] = [{ col: aCol, row: aRow }];
    const inBlob = new Set<string>([`${aCol},${aRow}`]);
    const target = 3 + Math.floor(rng() * 4); // 3–6 land cells
    const frontier = neighbors4(aCol, aRow);
    let safety = 0;
    while (blob.length < target && frontier.length > 0 && safety++ < 300) {
      const f = frontier.splice(Math.floor(rng() * frontier.length), 1)[0]!;
      const key = `${f.col},${f.row}`;
      if (inBlob.has(key) || !isWater(f.col, f.row)) continue;
      blob.push(f);
      inBlob.add(key);
      frontier.push(...neighbors4(f.col, f.row));
    }
    if (blob.length < 3) continue;

    // Perimeter = open water touching the blob — candidate rock spots.
    const perim: { col: number; row: number }[] = [];
    const seen = new Set<string>();
    for (const b of blob) {
      for (const n of neighbors4(b.col, b.row)) {
        const key = `${n.col},${n.row}`;
        if (inBlob.has(key) || seen.has(key) || !isWater(n.col, n.row)) continue;
        seen.add(key);
        perim.push(n);
      }
    }
    if (perim.length < 2) continue;

    // Commit the land.
    for (const b of blob) cells[b.row]![b.col]!.content = { kind: "land" };

    // Drop one or two rocks on the perimeter, each marked by a cardinal
    // on a water cell (the open-water/safe side — land cells are
    // skipped, so the mark naturally lands seaward).
    shuffleInPlace(perim, rng);
    const rockCount = 1 + Math.floor(rng() * 2); // 1–2
    let placed = 0;
    for (const p of perim) {
      if (placed >= rockCount) break;
      cells[p.row]![p.col]!.content = { kind: "hazard" };
      const dirs = [...ALL_DIRECTIONS];
      shuffleInPlace(dirs, rng);
      for (const dir of dirs) {
        const off = DIR_OFFSETS[dir];
        const cCol = p.col - off.dCol;
        const cRow = p.row - off.dRow;
        if (isWater(cCol, cRow)) {
          cells[cRow]![cCol]!.content = { kind: "cardinal", direction: dir };
          break;
        }
      }
      placed++;
    }
    return placed > 0;
  }
  return false;
}

// ─── Reveal ──────────────────────────────────────────────────────

export interface RevealResult {
  /// What just happened on this click. Drives the toast / fly-over
  /// message in the UI.
  event:
    | "water"
    | "cardinal"
    | "land"
    | "hit"
    | "sunk"
    | "grounded"
    | "already-revealed"
    | "game-over";
  /// If a ship was just sunk, this is its display name.
  sunkShipName?: string;
  /// Set on a "grounded" event when a cardinal marking this rock was
  /// already revealed — the player was warned and grounded anyway, so
  /// they lose two lives instead of one.
  doublePenalty?: boolean;
}

/// Apply a click to the board. Mutates `board` in place AND returns
/// the new board reference for React state callers — both styles
/// work.
export function revealCell(
  board: Board,
  col: number,
  row: number,
  options: { revealPaired?: boolean } = {},
): { board: Board; result: RevealResult } {
  if (board.status !== "playing") {
    return { board, result: { event: "game-over" } };
  }
  const cell = board.cells[row]?.[col];
  if (!cell) return { board, result: { event: "already-revealed" } };
  if (cell.state !== "hidden") {
    return { board, result: { event: "already-revealed" } };
  }

  board.shotsFired += 1;

  switch (cell.content.kind) {
    case "water":
      cell.state = "water";
      return { board, result: { event: "water" } };

    case "cardinal":
      // The cardinal is a warning only — the rock it marks stays
      // hidden. Read its direction to deduce where the rock is.
      cell.state = "cardinal";
      return { board, result: { event: "cardinal" } };

    case "land":
      // Land is safe. Revealing one cell uncovers the whole connected
      // landmass (solo only — PvP reveals just the fired cell); the
      // rocks among/around it stay hidden.
      if (options.revealPaired) {
        floodRevealLand(board, col, row);
      } else {
        cell.state = "land";
      }
      return { board, result: { event: "land" } };

    case "hazard": {
      cell.state = "grounded";
      // Two lives lost if a cardinal marking this rock was already
      // revealed — you were warned and ran onto it anyway. Otherwise
      // one (unmarked rock, or its mark not yet found).
      const warned = markingCardinalSeen(board, col, row);
      board.lives -= warned ? 2 : 1;
      if (board.lives <= 0) board.status = "lost";
      // Surface the cardinal that warned of this rock (solo only — PvP
      // reveals just the fired cell).
      if (options.revealPaired) revealMarkingCardinal(board, col, row);
      return { board, result: { event: "grounded", doublePenalty: warned } };
    }

    case "ship": {
      cell.state = "hit";
      const shipId = cell.content.shipId;
      const ship = board.ships.find((s) => s.spec.id === shipId);
      let sunkName: string | undefined;
      if (ship) {
        ship.hits += 1;
        if (ship.hits >= ship.cells.length) {
          ship.sunk = true;
          sunkName = ship.spec.name;
          // Flip every cell of this ship from "hit" to "sunk" so the
          // UI can render the silhouette in a different colour.
          for (const p of ship.cells) {
            board.cells[p.row]![p.col]!.state = "sunk";
          }
        }
      }
      if (board.ships.every((s) => s.sunk)) {
        board.status = "won";
      }
      return {
        board,
        result: sunkName
          ? { event: "sunk", sunkShipName: sunkName }
          : { event: "hit" },
      };
    }
  }
}

/// True when a cardinal marking the hazard at (col,row) has already
/// been revealed — the player saw the warning before grounding. A
/// cardinal of direction `dir` marking this rock sits at
/// hazard − DIR_OFFSETS[dir].
function markingCardinalSeen(board: Board, col: number, row: number): boolean {
  for (const dir of ALL_DIRECTIONS) {
    const off = DIR_OFFSETS[dir];
    const cardinal = board.cells[row - off.dRow]?.[col - off.dCol];
    if (
      cardinal &&
      cardinal.content.kind === "cardinal" &&
      cardinal.content.direction === dir &&
      cardinal.state === "cardinal"
    ) {
      return true;
    }
  }
  return false;
}

/// Flood-fill reveal the connected landmass containing (col,row). Only
/// land cells are traversed, so rocks among the land stay hidden.
function floodRevealLand(board: Board, col: number, row: number): void {
  const stack = [{ col, row }];
  while (stack.length > 0) {
    const { col: c, row: r } = stack.pop()!;
    const cell = board.cells[r]?.[c];
    if (!cell || cell.content.kind !== "land" || cell.state === "land") continue;
    cell.state = "land";
    stack.push(...neighbors4(c, r));
  }
}

/// Reveal any cardinal that marks the hazard just grounded on. A
/// cardinal of direction `dir` marking this hazard sits at
/// hazard − DIR_OFFSETS[dir].
function revealMarkingCardinal(board: Board, col: number, row: number): void {
  for (const dir of ALL_DIRECTIONS) {
    const off = DIR_OFFSETS[dir];
    const cardinal = board.cells[row - off.dRow]?.[col - off.dCol];
    if (
      cardinal &&
      cardinal.content.kind === "cardinal" &&
      cardinal.content.direction === dir &&
      cardinal.state === "hidden"
    ) {
      cardinal.state = "cardinal";
    }
  }
}

// ─── Coordinate helpers ─────────────────────────────────────────

/// Chart-style coordinate label, e.g. "C7". Columns A–Z then
/// AA, AB … so 100-wide boards still get sensible labels (A, B,
/// …, Z, AA, AB, …, CV for col 99).
export function coordLabel(col: number, row: number): string {
  let n = col + 1;
  let letters = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return `${letters}${row + 1}`;
}

export function totalShipCells(fleet: readonly ShipSpec[]): number {
  return fleet.reduce((sum, s) => sum + s.length, 0);
}
